import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import apiService from '../utils/api';
import tokenExpirationHandler from '../utils/tokenExpirationHandler';
import { ROLES } from '../constants';

interface User {
  id: number;
  first_name?: string;
  last_name?: string;
  user_name?: string;
  username?: string;
  email?: string;
  email_id?: string;
  profile_photo?: string;
  profile_picture?: string;
  profilePhoto?: string;
  avatar?: string;
  tenant_id?: number;
  tenant_code?: string;
  roles: string[];
  permissions: string[];
  subscriptionPermissions?: string[]; // Permissions from subscription plan
  subscriptionModules?: string[]; // Modules from subscription plan (e.g., EMPLOYEE, LEAVE, PAYROLL)
  subscriptionRoutes?: string[]; // Routes from subscription plan (e.g., 'payroll-components', 'timesheet-master')
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPermissionsLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshAccessToken: () => Promise<boolean>;
  hasRole: (roles: string[]) => boolean;
  hasPermission: (permissions: string[]) => boolean;
  hasSubscriptionPermission: (permissions: string[]) => boolean;
  hasSubscriptionModule: (modules: string[]) => boolean;
  hasSubscriptionRoute: (routeKey: string) => boolean;
  hasRouteAccess: (routeKey: string) => boolean;
  fetchSubscriptionPermissions: () => Promise<void>;
  fetchSubscriptionModules: () => Promise<void>;
  fetchSubscriptionRoutes: () => Promise<void>;
  fetchRoutePermissionsMapping: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [routePermissionsMapping, setRoutePermissionsMapping] = useState<Record<string, string[]> | null>(null);

  const isPermissionsLoading = !!user && (
    !user.subscriptionPermissions ||
    !user.subscriptionModules ||
    !user.subscriptionRoutes ||
    routePermissionsMapping === null
  );

  // Initialize token expiration handler
  useEffect(() => {
    tokenExpirationHandler.init(navigate, logout);
  }, [navigate]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          // Sync tenant context to localStorage so apiClient sends X-Tenant-Id (tenant-scoped APIs)
          if (parsedUser?.tenant_id != null) {
            localStorage.setItem('tenantId', String(parsedUser.tenant_id));
            if (parsedUser.tenant_code) localStorage.setItem('tenantCode', parsedUser.tenant_code);
          }
          
          // We now assume the user is authenticated if they have user data in localStorage.
          // In a real app, an initial /api/auth/me request is better.
          // But we will let the first API call's interceptor handle a 401 if the cookie expired.
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const verifyToken = async (token: string): Promise<boolean> => {
    try {
      // You can add a token verification endpoint or just check if token exists
      return !!token;
    } catch (error) {
      return false;
    }
  };

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      console.log('AuthContext - Attempting login for:', username);
      const response = await apiService.login(username, password);
      console.log('AuthContext - Login response:', response);
      
      // Backend returns direct data without success/data wrapper
      if (response && response.accessToken && response.refreshToken) {
        const { 
          accessToken: newAccessToken, 
          refreshToken: newRefreshToken, 
          id, 
          roles, 
          permissions,
          email,
          email_id,
          profile_photo,
          profile_picture,
          profilePhoto,
          avatar,
          tenant_id,
          tenant_code
        } = response;
        
        console.log('AuthContext - Token cookies should be set by backend now:', {
          tenant_id: tenant_id ?? 'none'
        });
        
        // Create user object from response data (include tenant for tenant-scoped API calls)
        const userData: User = {
          id,
          username, // Use the login username
          user_name: username, // Also set user_name for compatibility
          email: email || email_id,
          email_id: email_id || email,
          profile_photo: profile_photo || profile_picture || profilePhoto || avatar,
          profile_picture: profile_picture || profile_photo || profilePhoto || avatar,
          profilePhoto: profilePhoto || profile_picture || profile_photo || avatar,
          avatar: avatar || profile_picture || profile_photo || profilePhoto,
          roles,
          permissions,
          ...(tenant_id != null && { tenant_id }),
          ...(tenant_code && { tenant_code }),
        };
        
        setUser(userData);
        
        // Store user in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        if (newAccessToken) localStorage.setItem('accessToken', newAccessToken);
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
        
        if (tenant_id != null) {
          localStorage.setItem('tenantId', String(tenant_id));
          if (tenant_code) localStorage.setItem('tenantCode', tenant_code);
        } else {
          localStorage.removeItem('tenantId');
          localStorage.removeItem('tenantCode');
        }
        
        console.log('AuthContext - Tokens stored in localStorage and cookies');
        console.log('AuthContext - User data set:', userData);
        message.success('Login successful!');
        return true;
      } else {
        console.error('AuthContext - Invalid response structure:', response);
        message.error('Invalid response from server');
        return false;
      }
    } catch (error: any) {
      console.error('AuthContext - Login error:', error);
      message.error(error.message || 'Login failed');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch(e) {
      console.error('Logout error', e);
    }
    
    setUser(null);
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantCode');
    
    message.info('Logged out successfully');
  };

  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      console.log('Attempting to refresh access token...');
      const response = await apiService.refreshToken();
      
      // Backend returns direct data without success/data wrapper
      if (response && response.success) {
        const { roles, permissions, id } = response;
        
        console.log('Token refresh successful');
        
        // Create user object from response data
        const userData: User = {
          id,
          username: user?.username || 'user',
          user_name: user?.user_name || 'user',
          roles,
          permissions
        };
        
        setUser(userData);
        
        // Update localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        
        return true;
      } else {
        console.log('Invalid response from token refresh');
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  const hasRole = (roles: string[]): boolean => {
    if (!user || !user.roles) return false;
    
    // SUPER_ADMIN and TENANT_ADMIN bypass role checks
    const isSuperAdminOrTenantAdmin = user.roles?.some((role: string) => {
      const normalizedRole = role?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
      return normalizedRole === ROLES.SUPER_ADMIN || normalizedRole === 'SUPERADMIN' || normalizedRole === ROLES.TENANT_ADMIN;
    });
    
    if (isSuperAdminOrTenantAdmin) {
      return true;
    }

    // Normalize role names (case-insensitive, handle spaces/underscores/dashes)
    const normalizeRole = (role: string): string => {
      return role?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_') || '';
    };
    
    const userRolesNormalized = user.roles.map(normalizeRole);
    const requiredRolesNormalized = roles.map(normalizeRole);
    
    return userRolesNormalized.some(userRole => 
      requiredRolesNormalized.includes(userRole)
    );
  };

  const hasPermission = (permissions: string[]): boolean => {
    if (!user || !user.permissions) return false;
    
    // SUPER_ADMIN and TENANT_ADMIN bypass permission checks
    const isSuperAdminOrTenantAdmin = user.roles?.some((role: string) => {
      const normalizedRole = role?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
      return normalizedRole === ROLES.SUPER_ADMIN || normalizedRole === 'SUPERADMIN' || normalizedRole === ROLES.TENANT_ADMIN;
    });
    
    if (isSuperAdminOrTenantAdmin) {
      return true;
    }

    const normalizePermission = (p: string) => p?.toUpperCase().replace(/\./g, '_') || '';
    const requiredPermsNormalized = permissions.map(normalizePermission);

    // EMPLOYEE / CONTRACT_EMPLOYEE / INTERN roles implicitly get EMPLOYEE_READ.
    // Their JWT often has no explicit permissions assigned, but they must be able
    // to access the employee portal (all routes there require EMPLOYEE_READ).
    const EMPLOYEE_ROLES = ['EMPLOYEE', 'CONTRACT_EMPLOYEE', 'INTERN'];
    const hasEmployeeRole = user.roles?.some((role: string) =>
      EMPLOYEE_ROLES.includes(role?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_'))
    );
    if (hasEmployeeRole && requiredPermsNormalized.every(p => p === 'EMPLOYEE_READ')) {
      return true;
    }

    const userPermsNormalized = user.permissions.map(normalizePermission);
    
    // Check role-based permissions
    const hasRolePermission = userPermsNormalized.some(permission => requiredPermsNormalized.includes(permission));
    
    // Also check subscription permissions if available
    if (user.subscriptionPermissions && user.subscriptionPermissions.length > 0) {
      const userSubPermsNormalized = user.subscriptionPermissions.map(normalizePermission);
      const hasSubscriptionPerm = userSubPermsNormalized.some(permission => 
        requiredPermsNormalized.includes(permission)
      );
      return hasRolePermission && hasSubscriptionPerm;
    }
    
    return hasRolePermission;
  };

  const hasSubscriptionPermission = (permissions: string[]): boolean => {
    if (!user) return false;
    // If no subscription permissions loaded or empty array, assume access (no restriction)
    if (!user.subscriptionPermissions || user.subscriptionPermissions.length === 0) {
      return true;
    }
    
    // SUPER_ADMIN and TENANT_ADMIN bypass subscription permission checks
    const normalizedRoles = (user.roles || []).map((r: string) =>
      r?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
    );
    if (normalizedRoles.includes(ROLES.SUPER_ADMIN) || normalizedRoles.includes('SUPERADMIN') || normalizedRoles.includes(ROLES.TENANT_ADMIN)) {
      return true;
    }
    
    const normalizePermission = (p: string) => p?.toUpperCase().replace(/\./g, '_') || '';
    const requiredPermsNormalized = permissions.map(normalizePermission);
    const userSubPermsNormalized = user.subscriptionPermissions.map(normalizePermission);

    return userSubPermsNormalized.some(permission => requiredPermsNormalized.includes(permission));
  };

  const hasSubscriptionModule = (modules: string[]): boolean => {
    if (!user) return false;
    // If no subscription modules loaded or empty array, assume access (no restriction)
    if (!user.subscriptionModules || user.subscriptionModules.length === 0) {
      return true;
    }
    
    // SUPER_ADMIN and TENANT_ADMIN bypass subscription module checks (full menu)
    const normalizedRoles = (user.roles || []).map((r: string) =>
      r?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
    );
    if (normalizedRoles.includes(ROLES.SUPER_ADMIN) || normalizedRoles.includes('SUPERADMIN') || normalizedRoles.includes(ROLES.TENANT_ADMIN)) {
      return true;
    }
    
    return user.subscriptionModules.some(module => modules.includes(module));
  };

  const fetchSubscriptionPermissions = async (): Promise<void> => {
    try {
      const response = await apiService.getTenantPlanPermissions();
      const permissionCodes = (response?.success && response?.data && Array.isArray(response.data))
        ? response.data.map((p: any) => p?.code ?? p)
        : [];
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionPermissions: permissionCodes } : null);
    } catch (error) {
      console.error('Error fetching subscription permissions:', error);
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionPermissions: [] } : null);
    }
  };

  const fetchSubscriptionModules = async (): Promise<void> => {
    try {
      const response = await apiService.getTenantPlanModules();
      const modules = (response?.success && response?.data && Array.isArray(response.data))
        ? response.data
        : [];
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionModules: modules } : null);
    } catch (error) {
      console.error('Error fetching subscription modules:', error);
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionModules: [] } : null);
    }
  };

  const fetchSubscriptionRoutes = async (): Promise<void> => {
    try {
      const response = await apiService.getTenantPlanRoutes();
      const routes = (response?.success && response?.data && Array.isArray(response.data))
        ? response.data
        : [];
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionRoutes: routes } : null);
    } catch (error) {
      console.error('Error fetching subscription routes:', error);
      setUser(prevUser => prevUser ? { ...prevUser, subscriptionRoutes: [] } : null);
    }
  };

  const hasSubscriptionRoute = (routeKey: string): boolean => {
    if (!user) return false;
    // If no subscription routes loaded or empty array, assume access (no restriction)
    if (!user.subscriptionRoutes || user.subscriptionRoutes.length === 0) {
      return true;
    }
    
    // SUPER_ADMIN and TENANT_ADMIN bypass subscription route checks (full menu)
    const normalizedRoles = (user.roles || []).map((r: string) =>
      r?.toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_')
    );
    if (normalizedRoles.includes(ROLES.SUPER_ADMIN) || normalizedRoles.includes('SUPERADMIN') || normalizedRoles.includes(ROLES.TENANT_ADMIN)) {
      return true;
    }
    
    const CORE_HR_ROUTES = ['hr', 'leave-master', 'apply-leave', 'holiday-master', 'leave-allocation', 'attendance-records', 'shift-master', 'timesheet', 'weekly-timesheet-master', 'employee-master', 'employee-directory'];
    if (CORE_HR_ROUTES.includes(routeKey)) {
      return true;
    }
    return user.subscriptionRoutes.includes(routeKey);
  };

  const fetchRoutePermissionsMapping = async (): Promise<void> => {
    try {
      const response = await apiService.getRoutePermissionsMapping();
      if (response?.success && response?.data) {
        setRoutePermissionsMapping(response.data);
      } else {
        setRoutePermissionsMapping({});
      }
    } catch (error) {
      console.error('Error fetching route permissions mapping:', error);
      setRoutePermissionsMapping({});
    }
  };

  const DEFAULT_ROUTE_PERMISSIONS: Record<string, string[]> = {
    'hr': ['EMPLOYEE_READ', 'DEPARTMENT_READ', 'LEAVE_READ', 'LEAVE_APPROVE', 'ATTENDANCE_READ', 'ATTENDANCE_APPROVE'],
    'leave-master': ['LEAVE_READ', 'LEAVE_APPROVE'],
    'holiday-master': ['LEAVE_READ', 'LEAVE_APPROVE'],
    'leave-allocation': ['LEAVE_READ', 'LEAVE_APPROVE'],
    'apply-leave': ['LEAVE_READ', 'LEAVE_APPROVE', 'LEAVE_CREATE'],
    'leave-encashments': ['LEAVE_READ', 'LEAVE_APPROVE'],
    'leave-carry-forwards': ['LEAVE_READ', 'LEAVE_APPROVE'],
    'shift-master': ['SHIFT_READ', 'SHIFT_VIEW', 'SHIFT_UPDATE'],
    'attendance-records': ['ATTENDANCE_READ', 'ATTENDANCE_VIEW', 'ATTENDANCE_APPROVE'],
    'reimbursement-types': ['PAYROLL_VIEW', 'PAYROLL_READ'],
    'payroll': ['PAYROLL_VIEW', 'PAYROLL_READ'],
    'timesheet': ['TIMESHEET_READ', 'TIMESHEET_APPROVE'],
    'weekly-timesheet-master': ['TIMESHEET_READ', 'TIMESHEET_APPROVE'],
  };

  const hasRouteAccess = (routeKey: string): boolean => {
    const requiredPermissions = (routePermissionsMapping && routePermissionsMapping[routeKey])
      ? routePermissionsMapping[routeKey]
      : DEFAULT_ROUTE_PERMISSIONS[routeKey];
    
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    const mergedPermissions = DEFAULT_ROUTE_PERMISSIONS[routeKey]
      ? Array.from(new Set([...requiredPermissions, ...DEFAULT_ROUTE_PERMISSIONS[routeKey]]))
      : requiredPermissions;

    return hasPermission(mergedPermissions);
  };

  // Fetch subscription permissions, modules, and routes after login
  useEffect(() => {
    if (user) {
      if (!user.subscriptionPermissions) {
        fetchSubscriptionPermissions();
      }
      if (!user.subscriptionModules) {
        fetchSubscriptionModules();
      }
      if (!user.subscriptionRoutes) {
        fetchSubscriptionRoutes();
      }
      if (routePermissionsMapping === null) {
        fetchRoutePermissionsMapping();
      }
    }
  }, [user, routePermissionsMapping]);

  const value: AuthContextType = {
    user,
    accessToken: null, // deprecated
    refreshToken: null, // deprecated
    isAuthenticated: !!user,
    isLoading,
    isPermissionsLoading,
    login,
    logout,
    refreshAccessToken,
    hasRole,
    hasPermission,
    hasSubscriptionPermission,
    hasSubscriptionModule,
    hasSubscriptionRoute,
    hasRouteAccess,
    fetchSubscriptionPermissions,
    fetchSubscriptionModules,
    fetchSubscriptionRoutes,
    fetchRoutePermissionsMapping,
  };

  console.log('AuthContext Debug:', {
    user,
    isAuthenticated: !!user,
    isLoading
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 